import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { nanoid } from 'nanoid';
import { isEqual } from 'lodash';
import TextInput from '../../componentsV2/FormInputs/TextInput/TextInput';
import TextArea from '../../componentsV2/FormInputs/TextArea/TextArea';
import CustomSelect from '../../componentsV2/FormInputs/CustomSelect/CustomSelect';
import Button from '../../componentsV2/Button/Button';
import { createVisit, getVisit, updateVisit } from '../../utils/api/visits';
import { redirectToSiteVisit, redirectToPreviousPage, redirectToSite } from '../../utils/navigation';
import {
  useToasts,
  useUnsavedChangesWarning,
  useCloseDialogWithUnsavedChanges,
  useCustomFieldErrorScroll,
} from '../../hooks';
import CustomFieldsForm from '../../componentsV2/FormInputs/CustomFieldsForm';
import { getModelForm } from '../../utils/api/modelForm';
import { DateSelectorV2, Number } from '../../componentsV2/FormInputs';
import DistributedItems from '../../componentsV2/Visits/DistributedItems';
import { formatVisitAndNoteDate } from '../../utils/dates';
import { getDistributedItemsProducts } from '../../utils/api/distributedItemsProducts';
import HeaderIconTooltip from '../../componentsV2/HeaderIconTooltip/HeaderIconTooltip';
import { itemTypes } from '../../utils/customFieldsUtils';

const VisitForm = ({
  visitId = null, organizationId, siteName, siteId, onPurposeChange,
}) => {
  const [originalVisitData, setOriginalVisitData] = useState({});
  const [visitData, setVisitData] = useState({});
  const [distributedItemProducts, setDistributedItemProducts] = useState([]);
  const [isSendingForm, setIsSendingForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    date: '',
    completed: '',
    purpose: '',
    workers_interacted: '',
    total_workers: '',
  });
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [shouldScrollToError, setShouldScrollToError] = useState(false);
  const triggerCustomFieldErrorScroll = useCustomFieldErrorScroll(setShouldScrollToError);

  const { showToast } = useToasts();

  const [hasCustomFieldsChanged, setHasCustomFieldsChanged] = useState(false);

  const hasUnsavedChanges = !isEqual(visitData, originalVisitData) || hasCustomFieldsChanged;

  const validateRequiredFields = (data) => {
    const errors = {};
    if (!data.date) errors.date = 'Date is required';
    if (!data.completed) errors.completed = 'Status is required';
    if (!data.purpose) errors.purpose = 'Purpose is required';
    if (data.workers_interacted < 0) {
      errors.workers_interacted = "Can't be negative";
    }
    if (data.total_workers < 0) {
      errors.total_workers = "Can't be negative";
    }

    data.custom_fields?.forEach((field) => {
      const isFieldEmpty = Array.isArray(field.value) ? field.value.length === 0 : !field.value;

      if (field.required && isFieldEmpty) {
        errors[`custom_fields.${field.label}`] = `${field.label} is required`;
      }
    });
    return errors;
  };

  useEffect(() => {
    const errors = validateRequiredFields(visitData);
    setErrorMessage(errors);
  }, [visitData]);

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, visitData, hasCustomFieldsChanged]); // hasCustomFieldsChanged, visitData - track changes to visitData, hasCustomFieldsChanged for unsaved changes if isUnsavedChanges was set to false on previous changes

  useUnsavedChangesWarning(isUnsavedChanges);

  // On click Save we check for visitId to see if it's an un updated or new visit
  const onSave = () => {
    const hasMissingProductId = visitData.distributed_items?.some((item) => !item.product_id);
    if (hasMissingProductId) {
      showToast({ message: 'Please select a valid product for all distributed items.', type: 'error' });
      return;
    }

    const errors = validateRequiredFields(visitData);

    if (Object.keys(errors).length > 0) {
      setIsSendingForm(false);
      const hasTopLevelErrors = Object.keys(errors).some((key) => !key.startsWith('custom_fields.'));
      triggerCustomFieldErrorScroll(hasTopLevelErrors);
      return;
    }

    setIsSendingForm(true);

    const formattedData = { ...visitData };
    if (visitData.completed?.toLowerCase() === 'completed') formattedData.completed = true;
    else formattedData.completed = false;

    // Label for customs field to lower case
    formattedData.custom_fields = formattedData.custom_fields?.map((field) => ({
      ...field,
      label: field.label.toLowerCase(),
    }));

    formattedData.custom_fields = formattedData.custom_fields?.map((field) => {
      if (field.type === 'checkbox-group' && Array.isArray(field.value)) {
        return {
          ...field,
          value: JSON.stringify(field.value),
        };
      }
      return field;
    });

    const apiRequest = visitId ? updateVisit : createVisit;
    const formData = formattedData;

    apiRequest(formData, organizationId, visitId)
      .then((response) => {
        if (response.status === 200) {
          showToast({ message: 'Visit has been saved', type: 'success' });
        }
        if (response.status === 201) {
          showToast({ message: 'Visit has been created', type: 'success' });
        }
        return response.json();
      })
      .then((data) => {
        if (!data.visits) {
          if (Array.isArray(data)) {
            data.map((error) => showToast({ message: error, type: 'error' }));
          }
        } else {
          setIsUnsavedChanges(false);
          setTimeout(() => {
            redirectToSiteVisit(data.visits.site_id, data.visits.id);
            setIsSendingForm(false);
            setIsUnsavedChanges(false);
          }, 1000);
        }
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      })
      .finally(() => {
        setIsSendingForm(false);
      });
  };

  // Callback function to redirect to the site visit page or previous page
  const callback = visitId ? () => redirectToSiteVisit(siteId, visitId) : () => redirectToPreviousPage(redirectToSite);

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: callback,
  });
  const onCancel = () => {
    if (hasUnsavedChanges) {
      handleCloseDialog();
    } else {
      callback();
    }
  };

  useEffect(() => {
    const defaultData = {
      id: null,
      site_id: null,
      site_name: '',
      date: formatVisitAndNoteDate(new Date()), // Default date is today for new visits prepopulated
      purpose: '',
      user_id: '',
      creator: '',
      leader: '',
      completed: 'Completed', // Default status is Completed for new visits prepopulated
      visitors: '',
      provided: '',
      total_workers: 0,
      workers_interacted: 0,
      needs_concerns: '',
      other_notes: '',
      distributed_items: [],
      created_at: '',
      updated_at: '',
      routes_id: null,
      custom_fields: [],
    };

    getDistributedItemsProducts(organizationId).then((distributedItemsData) => {
      setDistributedItemProducts(distributedItemsData.products);
      // We check visitID to update inputs otherwise is a new visit
      if (visitId) {
        getVisit(organizationId, visitId).then(async (visitDataResp) => {
          let customFields = visitDataResp.visit.custom_fields?.map((item, index) => ({
            ...item,
            position: index + 1,
            form_item_id: item.key,
            value:
              item.value
              || item.values
                ?.filter((v) => v.selected === true)
                .map((v) => v.value)
                .join(', ')
              || '',
          }));
          // This in case there are no values for custom fields and are saved like an empty array.
          // If it's the case we need to pull the model form info doing another api
          if (customFields.length === 0) {
            customFields = await getModelForm('visit', organizationId).then((customFieldsData) => {
              const newCustomFields = customFieldsData.form_items?.map((item, index) => ({
                ...item,
                position: index + 1,
                form_item_id: item.key,
              }));
              return newCustomFields;
            });
          }

          const distributedItems = visitDataResp.visit.distributed_items?.map((item) => {
            // Find the product by matching the product name
            const distribItems = distributedItemsData.products?.find((product) => product.name === item.product_name);

            return {
              ...item,
              id: nanoid(), // Generate a unique ID for each item to allow individual updates, changes, and deletions in the DistributedItems component
              category: distribItems?.name || null,
              product_id: distribItems?.id || null, // Add product id for PUT request
            };
          });

          const completedFormatted = visitDataResp.visit.completed === true ? 'Completed' : 'Attempted';
          const visit = {
            ...visitDataResp.visit,
            custom_fields: customFields,
            distributed_items: distributedItems,
            completed: completedFormatted,
          };
          setVisitData(visit);
          setOriginalVisitData(visit);
        });
      } else {
        getModelForm('visit', organizationId)
          .then((response) => response.json())
          .then((data) => {
            const activeItems = data.form_items?.filter((item) => item.active);
            const customFields = activeItems.map((item, index) => {
              if (itemTypes[item.type]) {
                return {
                  key: item.id,
                  type: itemTypes[item.type],
                  label: item.label,
                  position: index + 1,
                  value: '',
                  values: item.options?.map((option) => ({ value: option, selected: false, label: option })),
                  form_item_id: item.id,
                  required: item.required,
                  isEncrypted: item.encrypt,
                };
              }
              return item;
            });
            if (siteId) {
              setVisitData({
                ...defaultData,
                site_id: siteId,
                site_name: siteName,
                custom_fields: customFields,
              });
              setOriginalVisitData({
                ...defaultData,
                site_id: siteId,
                site_name: siteName,
                custom_fields: customFields,
              });
            } else {
              setVisitData({
                ...defaultData,
                site_name: siteName,
                custom_fields: customFields,
              });
              setOriginalVisitData({ ...defaultData, custom_fields: customFields });
            }
          });
      }
    });
  }, [visitId, organizationId, siteId, siteName]);

  const updateItemsWithIds = (items, products) => {
    return items?.map((item) => {
      // Read `item.category` (renamed from `item.item`) to match against product names
      const matchedProduct = products?.find((product) => product.name === item.category);
      return {
        ...item,
        product_id: matchedProduct ? matchedProduct.id : null,
      };
    });
  };

  const handleVisitDataChange = (field, value) => {
    setVisitData((prev) => {
      // Only update state if the new value is different
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
    // clear the error message for the field being changed
    setErrorMessage((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  React.useEffect(() => {
    if (onPurposeChange) {
      onPurposeChange(visitData.purpose || '');
    }
  }, [visitData.purpose, onPurposeChange]);

  return (
    <div className="md:w-9/12 lg:w-7/12 ml-0">
      <div className="flex justify-between items-center border-b border-vamos-blue-gray pb-2 mb-4 mt-8">
        <h2 className="font-bold text-2xl text-vamos-black font-sans text-24 font-bold">
          {visitData.purpose || 'New Visit'}
        </h2>
        <div className="flex space-x-2">
          <Button label="Cancel" size="md" onClick={onCancel} color="secondary" />
          <Button label={`${visitId ? 'Save' : 'Create'}`} size="md" onClick={onSave} isDisabled={isSendingForm} />
        </div>
      </div>
      <main className="space-y-8">
        <div className="max-w-46.5">
          <DateSelectorV2
            label="Date"
            name="date"
            value={visitData.date}
            setValue={(value) => handleVisitDataChange('date', value === '' ? value : formatVisitAndNoteDate(value))}
            isRequired
            placeholder="visit date"
            errorMessage={errorMessage.date}
            asteriskWidth="w-2"
            fontSize="text-sm"
            inputStyle={{ paddingLeft: 12 }}
            spaceY="space-y-0.5"
            inputHeight="h-10"
            iconRight={4}
            iconBottom={4}
            errorMessageClass="absolute"
            dataTestId="visit-date-input"
          />
        </div>

        <div className="max-w-46.5">
          <CustomSelect
            options={[{ label: 'Completed' }, { label: 'Attempted' }]}
            label="Status"
            value={visitData.completed}
            defaultValue="Completed"
            setValue={(opt) => handleVisitDataChange('completed', opt?.label || '')}
            isRequired
            errorMessage={errorMessage.completed}
            fontSize={14} // font size in px
            spaceY="space-y-0.5"
            tooltip={(
              <HeaderIconTooltip
                divTextStart="“Completed” indicates a visit was carried"
                divTextEnd={(
                  <>
                    <div>out successfully and you spoke to </div>
                    <div>workers. </div>
                    <br />
                    <div>“Attempted” indicates you tried to visit </div>
                    <div>but something got in the way or workers </div>
                    <div>weren’t home, and you may need to </div>
                    <div>return. </div>
                  </>
                )}
                iconName="icon_circle"
                border=""
                margins=""
                place="right"
                paddingBottom=""
                tooltipId="Status-tooltip"
                iconSize="h-3.5 w-3.5"
                tooltipClassName="z-10"
                id="status"
              />
            )}
            inputHeight={40}
            errorMessageClass="absolute"
          />
        </div>

        <TextInput
          label="Purpose"
          name="purpose"
          value={visitData.purpose}
          setValue={(value) => handleVisitDataChange('purpose', value)}
          isRequired
          errorMessage={errorMessage.purpose}
          asteriskWidth="w-2"
          placeholder="enter purpose"
          fontSize="text-sm"
          inputHeight="h-10"
          errorMessageClass="absolute"
          errorMessageZIndexClass=""
          spaceY="space-y-0.5"
          dataTestId="purpose-input"
        />

        <TextInput
          label="Outreach Leader"
          name="leader"
          value={visitData.leader}
          setValue={(value) => setVisitData((prev) => ({ ...prev, leader: value }))}
          placeholder="who led this outreach visit?"
          fontSize="text-sm"
          inputHeight="h-10"
          spaceY="space-y-0.5"
        />

        <TextInput
          label="Additional Visitors"
          name="visitors"
          value={visitData.visitors}
          setValue={(value) => handleVisitDataChange('visitors', value)}
          placeholder="who else participated in this visit?"
          fontSize="text-sm"
          inputHeight="h-10"
          spaceY="space-y-0.5"
        />

        <div className="max-w-max space-y-8 mb-4">
          <Number
            label="Workers Reached"
            name="workersReached"
            value={visitData.workers_interacted}
            setValue={(value) => handleVisitDataChange('workers_interacted', value)}
            placeholder="# of workers you met"
            fontSize="text-sm"
            min={0}
            errorMessage={errorMessage.workers_interacted}
            inputHeight="h-10"
            spaceY="space-y-0.5"
          />

          <Number
            label="Total Workers"
            name="totalWorkers"
            value={visitData.total_workers}
            setValue={(value) => handleVisitDataChange('total_workers', value)}
            placeholder="# of workers at site"
            fontSize="text-sm"
            min={0}
            errorMessage={errorMessage.total_workers}
            spaceY="space-y-0.5"
            inputHeight="h-10"
          />
        </div>

        <TextArea
          label="Visit Summary"
          name="visitSummary"
          className="p-2"
          labelClassName="text-sm "
          placeholder="enter visit summary"
          value={visitData.other_notes}
          setValue={(value) => handleVisitDataChange('other_notes', value)}
          fontSize="text-sm"
          spaceY="space-y-0.5"
          rows={3}
        />

        <TextArea
          label="Needs/Concerns"
          name="needsConcerns"
          className="p-2"
          labelClassName="text-sm"
          placeholder="enter any needs or concerns"
          value={visitData.needs_concerns}
          setValue={(value) => handleVisitDataChange('needs_concerns', value)}
          spaceY="space-y-0.5"
          fontSize="text-sm"
        />
      </main>
      <div className="mt-5">
        {visitData.custom_fields && (
          <div>
            <CustomFieldsForm
              isEdit
              customFields={visitData.custom_fields}
              setData={setVisitData}
              isEditForm={!!visitId}
              iconSize="h-3.5 w-3.5"
              labelFontClassName="text-2xl font-medium"
              border=""
              headerBottomMargin="mb-4"
              margins=""
              paddingBottom=""
              errorMessage={errorMessage}
              inputFieldsHeight="h-10"
              inputHeightSelect={40}
              iconRight={4}
              iconBottom={4}
              errorMessageClass="absolute"
              className="mb-4"
              setCustomFieldsChanged={setHasCustomFieldsChanged}
              shouldScrollToError={shouldScrollToError}
              customFieldMaxWidths={{ date: 'max-w-46.5' }}
            />
          </div>
        )}
        <div className="pb-26">
          <DistributedItems
            isEdit
            isShowHeader={false}
            options={distributedItemProducts}
            distItems={visitData.distributed_items}
            setDistItems={(value) => {
              const updatedItems = updateItemsWithIds(value, distributedItemProducts);
              setVisitData((prev) => ({ ...prev, distributed_items: updatedItems }));
            }}
            iconSize="h-3.5 w-3.5"
            labelFontClassName="text-2xl font-medium"
            border=""
            headerBottomMargin="mb-4"
            margins="mt-7"
            paddingBottom=""
          />

          <div className="flex justify-end items-center border-t border-vamos-blue-gray pb-2 mb-4 mt-8">
            <div className="flex justify-end space-x-2 pt-6 pb-8">
              <Button label="Cancel" size="md" onClick={onCancel} color="secondary" />
              <Button label={`${visitId ? 'Save' : 'Create'}`} size="md" onClick={onSave} isDisabled={isSendingForm} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

VisitForm.propTypes = {
  visitId: PropTypes.number,
  organizationId: PropTypes.number.isRequired,
  siteName: PropTypes.string.isRequired,
  siteId: PropTypes.number.isRequired,
  onPurposeChange: PropTypes.func,
};

export default VisitForm;
